import { TYPE_META, fmtMoney, fmtDate, relDaysLabel, statusMeta } from './vencimientosConfig';

/* Tabla compacta. Columnas: Tipo · Cliente · Concepto/Referencia · Monto(der) ·
   Vence · Estado · Acciones(hover). Click en fila -> abre el drawer con ese
   user_id (y uid de foco). Las acciones rapidas viven en la ultima columna y
   se revelan al hacer hover sobre la fila. */

// Estados "cerrados": la accion rapida pasa a ser "Revertir".
const DONE_STATES = new Set(['pagado', 'renovado', 'condonado']);

/* Decide el toggle rapido de una fila segun su tipo+estado.
   Devuelve null cuando no aplica toggle (cancelado). title = tooltip/aria. */
function quickActionFor(r) {
  if (r.estado === 'cancelado') return null;
  if (DONE_STATES.has(r.estado)) {
    return { paid: false, title: 'Revertir', icon: 'pi-undo', variant: 'plain' };
  }
  if (r.tipo === 'placa') {
    return { paid: true, title: 'Marcar renovada', icon: 'pi-check', variant: 'ok' };
  }
  return { paid: true, title: 'Marcar pagado', icon: 'pi-check', variant: 'ok' };
}

function StatusBadge({ estado, needsReview }) {
  const meta = statusMeta(estado, needsReview);
  return (
    <span className={`venc-badge tone-${meta.tone}`}>
      <i className={`pi ${meta.icon}`} />{meta.label}
    </span>
  );
}

function TypeCell({ tipo }) {
  const meta = TYPE_META[tipo];
  if (!meta) return <span className="cell-type">{tipo}</span>;
  return (
    <div className="cell-type">
      <span className={`tico ${meta.toneClass}`}><i className={`pi ${meta.icon}`} /></span>
      {meta.label}
    </div>
  );
}

function WhenCell({ fecha, dias, estado }) {
  const rel = relDaysLabel(dias, estado);
  return (
    <span className="cell-when">
      {fmtDate(fecha)}
      {rel && <span className={`rel ${rel.cls}`}>{rel.text}</span>}
    </span>
  );
}

/* Acciones rapidas por fila. stopPropagation para no disparar el onClick de la
   fila (que abre el drawer). El boton principal hace el toggle del tipo; el
   icono "ojo" abre el detalle para las acciones pesadas (editar prima, etc.). */
function ActionsCell({ row, busy, onQuickAction, onOpen }) {
  const act = quickActionFor(row);
  return (
    <div className="cell-actions" onClick={(e) => e.stopPropagation()}>
      {act && (
        <button
          type="button"
          className={`venc-iconbtn ${act.variant}`}
          disabled={busy}
          title={act.title}
          aria-label={act.title}
          onClick={() => onQuickAction(row, act.paid)}
        >
          <i className={`pi ${busy ? 'pi-spin pi-spinner' : act.icon}`} />
        </button>
      )}
      <button
        type="button"
        className="venc-iconbtn ghost"
        title="Abrir detalle"
        aria-label="Abrir detalle"
        disabled={busy}
        onClick={() => onOpen(row.user_id, row.uid)}
      >
        <i className="pi pi-eye" />
      </button>
    </div>
  );
}

export default function FeedTable({ rows = [], onOpen, onQuickAction, busyUid }) {
  if (!rows.length) {
    return (
      <div className="venc-empty">
        <i className="pi pi-inbox" />
        No hay vencimientos con estos filtros.
      </div>
    );
  }

  return (
    <table className="venc-table">
      <thead>
        <tr>
          <th>Tipo</th>
          <th>Cliente</th>
          <th>Concepto</th>
          <th className="right">Monto</th>
          <th>Vence</th>
          <th>Estado</th>
          <th className="right acts">Acciones</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const key = r.uid || r.id;
          return (
            <tr
              key={key}
              className={r.estado === 'vencido' ? 'is-overdue' : ''}
              onClick={() => onOpen(r.user_id, r.uid)}
            >
              <td><TypeCell tipo={r.tipo} /></td>
              <td>
                <div className="cell-client">
                  <b>
                    {r.cliente}
                    {r.needs_review && (
                      <i className="pi pi-flag-fill row-flag" title="Fila por revisar" />
                    )}
                    {r.contrato_recuperado && (
                      <span
                        className="row-recuperado"
                        title={`Contrato cerrado por recuperación de auto${r.recuperacion_fecha ? ` (${r.recuperacion_fecha})` : ''}. Nada se canceló.`}
                      >
                        Recuperado
                      </span>
                    )}
                  </b>
                  <span>{r.referencia || '—'}</span>
                </div>
              </td>
              <td>
                <div className="cell-ref">
                  <b>{r.concepto || (r.tipo === 'seguro' ? `Poliza ${r.poliza || '—'}` : 'Placas')}</b>
                  <span>
                    {r.tipo === 'seguro'
                      ? [r.aseguradora, r.periodo].filter(Boolean).join(' · ') || '—'
                      : [r.vehiculo, r.periodo].filter(Boolean).join(' · ') || '—'}
                  </span>
                </div>
              </td>
              <td className="right">
                <span className="cell-amount">{fmtMoney(r.monto, r.moneda)}</span>
              </td>
              <td><WhenCell fecha={r.fecha_vencimiento} dias={r.dias_para_vencer} estado={r.estado} /></td>
              <td><StatusBadge estado={r.estado} needsReview={r.needs_review} /></td>
              <td className="acts">
                <ActionsCell
                  row={r}
                  busy={busyUid === key}
                  onQuickAction={onQuickAction}
                  onOpen={onOpen}
                />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
